<template>
  <div :class="embedded ? '' : 'container-fluid'">
    <div class="row">
      <div class="col-sm-12">
        <div :class="embedded ? '' : 'card'">
          <div v-if="!embedded" class="card-header d-flex justify-content-between align-items-center">
            <h4 class="card-title mb-0">{{ pageTitle }}</h4>
          </div>
          <div :class="embedded ? '' : 'card-body'">
            <div class="d-flex flex-wrap align-items-center mb-2 inv-rec__gap">
              <label class="mb-0 small">货主(화주)</label>
              <input v-model.trim="companyid" list="invRecCompanies" placeholder="全部(전체)" class="form-control form-control-sm" style="width:160px" @keyup.enter="load">
              <datalist id="invRecCompanies">
                <option v-for="c in companyOptions" :key="c" :value="c"></option>
              </datalist>
              <b-button size="sm" variant="primary" :disabled="loading" @click="load">搜索<small>(검색)</small></b-button>
              <b-spinner v-if="loading" small></b-spinner>
              <label class="mb-0 small text-nowrap"><input v-model="showReplaced" type="checkbox" class="mr-1" @change="load">显示已覆盖(덮어쓴 옛 기록 보기)</label>
              <small class="text-muted">
                在「出库列表 → 生成箱单发票」下载箱单发票·产地证后自动出现在这里(출고리스트 → 箱单发票 생성에서 내려받으면 여기에 자동으로 쌓입니다). 一个箱单发票一行，多个产地证也在同一行(箱单发票 하나 = 한 행, 产地证 여러 장도 같은 행).
              </small>
            </div>
            <!-- 날짜 일괄 다운로드 — 그날 箱单发票·提单·出库资料文件·产地证正本 을 zip 하나로, 세트(행)마다 폴더(2026-10-07 YZY) -->
            <div class="d-flex flex-wrap align-items-center mb-2 inv-rec__gap inv-rec__dl">
              <label class="mb-0 small">日期(날짜)</label>
              <select v-model="dl.date" class="form-control form-control-sm" style="width:140px">
                <option v-for="d in dateOptions" :key="d" :value="d">{{ d }} ({{ dateCount[d] }})</option>
              </select>
              <label v-for="t in dlTypes" :key="t.key" class="mb-0 small text-nowrap"><input v-model="dl.types" type="checkbox" :value="t.key" class="mr-1">{{ t.label }}</label>
              <b-button size="sm" variant="success" :disabled="dl.busy || !dl.date || !dl.types.length" @click="downloadDay">
                <b-spinner v-if="dl.busy" small></b-spinner> ⬇ 打包下载(zip 일괄 다운)
              </b-button>
              <small v-if="dl.busy" class="text-primary">{{ dl.progress }}</small>
              <!-- 按货主拆分出库清单 — 그날 저장된 출고리스트를 L열 화주마다 한 파일로(生成箱单发票 에서 옮겨 옴, 2026-10-07 YZY) -->
              <b-button size="sm" variant="outline-secondary" :disabled="dl.ownerBusy || !dl.date" title="当天保存的出库清单按货主(L列)拆成一个货主一个文件(그날 저장된 출고리스트를 화주마다 한 파일로)" @click="downloadOwnerLists">
                <b-spinner v-if="dl.ownerBusy" small></b-spinner> ⬇ 按货主拆分出库清单(화주별 출고리스트)
              </b-button>
            </div>
            <div v-if="error" class="text-danger small mb-2">{{ error }}</div>

            <div class="table-responsive inv-rec__table">
              <table class="table table-sm table-bordered mb-0">
                <thead class="thead-light">
                  <tr>
                    <th>日期(날짜)
                      <!-- coupang托盘表 — 出库那天 기준. 기본은 当天(오늘 출고분)만, 이전 날짜는 날짜를 골라야 보인다(2026-10-12 YZY) -->
                      <div class="mt-1"><b-button size="sm" variant="outline-primary" class="py-0" @click="openPallet">coupang托盘表</b-button></div></th><th style="min-width:150px">货主(화주)</th><th style="min-width:260px">托盘号(파렛트)</th><th>件数·毛重·体积</th>
                    <th>箱单发票</th>
                    <!-- 核对件·正本·提单 은 제목에서 여러 파일을 한 번에 — 파일 이름·내용으로 행·产地证 을 자동으로 맞춘다(2026-10-07 YZY) -->
                    <th style="min-width:330px">产地证(원산지증명) · 核对件 · 正本
                      <div class="mt-1">
                        <b-button size="sm" variant="outline-primary" class="py-0 mr-1" :disabled="batch.running || batch.saving" @click="pickBatch('check')">⬆ 批量上传核对件(일괄 대조)</b-button>
                        <b-button size="sm" variant="outline-primary" class="py-0" :disabled="batch.running || batch.saving" @click="pickBatch('original')">⬆ 批量上传正本(일괄 정본)</b-button>
                      </div>
                    </th>
                    <th style="min-width:190px">提单BL
                      <div class="mt-1"><b-button size="sm" variant="outline-primary" class="py-0" :disabled="batch.running || batch.saving" @click="pickBatch('bl')">⬆ 批量上传提单(일괄 BL)</b-button></div>
                    </th>
                    <th>出库资料文件</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  <!-- 순서 = 方数计划 날짜(최근 먼저) → 일괄 생성 순번(2026-10-06 YZY) -->
                  <!-- 날짜별로 굵은 검은 테두리로 묶는다(2026-10-07 YZY) — 같은 日期 행들이 한 상자 -->
                  <tr v-for="(r, i) in sortedList" :key="r._id" class="inv-rec__day" :class="dayEdgeClass(i)">
                    <!-- 日期 = 方数计划 날짜(없는 옛 기록은 서류 날짜) -->
                    <!-- #N = 그날 方数计划 파일 순서(2026-10-07 YZY, 第N单 → #N). 계획표에 없는 기록만 옛 순번 #N*(일괄 생성 세트 번호 = 파일명 순번) — 별표로 구분 -->
                    <td class="text-nowrap">{{ dateOf(r) }}<span v-if="planRank[r._id]" v-b-tooltip.hover class="badge badge-dark ml-1" :title="planTip(r)">#{{ planRank[r._id] }}</span><span v-else-if="r.seq" v-b-tooltip.hover class="badge badge-secondary ml-1" :title="seqTip()">#{{ r.seq }}*</span>
                      <span v-if="r.replacedBy" class="badge badge-warning ml-1" :title="'被新的箱单发票覆盖(새 서류로 덮어씀): ' + r.replacedBy">已覆盖(덮어씀)</span>
                      <div class="small text-muted">{{ fmtDate(r.updatedAt) }}</div></td>
                    <!-- 货主 칸은 항상 3줄(2026-10-07 YZY) — 货主 / 清关方式 / 收货人. 값이 없으면 「—」, 줄 높이 고정이라 행마다 같은 자리 -->
                    <td class="inv-rec__own">
                      <div class="inv-rec__own-line" :title="mergedOf(r).length ? r.companyid + ' = ' + mergedOf(r).join(' + ') : r.companyid">{{ r.companyid || '—' }}<span v-if="mergedOf(r).length" class="small text-muted"> (合并 {{ mergedOf(r).join('+') }})</span></div>
                      <div class="inv-rec__own-line">
                        <span v-if="r.clearanceLabel" class="inv-rec__clr" :class="'inv-rec__clr--' + r.clearance">{{ r.clearanceLabel }}</span>
                        <span v-else class="text-muted">—</span>
                      </div>
                      <div class="inv-rec__own-line small text-muted" :title="importerName(r)">收货人 {{ importerName(r) || '—' }}</div>
                    </td>
                    <!-- 散货 는 箱号 를 같이(2026-10-06 YZY) -->
                    <td class="small inv-rec__plt"><span v-for="(t, ti) in palletLines(r)" :key="r._id + 'p' + ti">{{ t }}<template v-if="ti < palletLines(r).length - 1">, </template></span></td>
                    <td class="small text-nowrap">
                      {{ r.summary && r.summary.packages }} · {{ r.summary && r.summary.qty }}PCS<br>
                      {{ num2(r.summary && r.summary.gw) }}kg · {{ num2(r.summary && r.summary.cbm) }}CBM
                    </td>
                    <td class="text-nowrap"><b-button size="sm" variant="outline-success" :disabled="busyId === r._id" @click="downloadInvoice(r)">⬇ 下载(다운)</b-button></td>
                    <td class="small">
                      <!-- 箱单发票 총금액 230 USD 미만 = 产地证 불필요(2026-10-07 YZY) -->
                      <div v-if="cooNotNeeded(r)" class="font-weight-bold" :style="cooStyle(cooNoneCode)">{{ cooNotNeededText }}(원산지증명 불필요)</div>
                      <div v-else-if="!r.coo || !r.coo.files || !r.coo.files.length" class="text-muted">未生成(아직 안 만듦)</div>
                      <!-- 이미 만든 产地证 다운로드 — 箱单发票 처럼 기록으로 다시 만든다(2026-10-07 YZY). 「全部产地证下载」 버튼은 뺐다(2026-10-07 YZY) -->
                      <div v-for="f in (r.coo && r.coo.files) || []" :key="r._id + 'co' + f.no" class="inv-rec__coo">
                        <b>CO{{ f.no }}</b> <span class="px-1 rounded" :style="cooStyle(f.type)">{{ cooLabel(f.type) }}</span><span v-if="f.total > 1">-{{ f.fileNo }}</span> · {{ f.items.length }}品
                        <b-button size="sm" variant="outline-success" class="ml-1 py-0" :disabled="busyId === r._id" @click="downloadCoo(r, f)">⬇ 下载(다운)</b-button>
                        <span class="ml-2">
                          <template v-if="f.check">
                            <a href="#" :class="statusClass(f.check.compare)" title="点击看差异(눌러서 항목별 차이 보기)" @click.prevent="toggleCmp(r._id + 'co' + f.no + 'check')">核对 {{ checkText(f.check.compare) }}</a>
                            <a href="#" class="ml-1" @click.prevent="downloadStored(f.check.file)">📎</a>
                          </template>
                          <a href="#" class="ml-1" @click.prevent="openCheck(r, 'coo', f)">{{ f.check ? '重新核对(다시)' : '上传核对件(대조)' }}</a>
                        </span>
                        <span class="ml-2">
                          <template v-if="f.original">
                            <a href="#" :class="statusClass(f.original.compare)" title="点击看差异(눌러서 항목별 차이 보기)" @click.prevent="toggleCmp(r._id + 'co' + f.no + 'original')">正本 {{ checkText(f.original.compare) }}</a>
                            <a href="#" class="ml-1" @click.prevent="downloadStored(f.original.file)">📎</a>
                          </template>
                          <a href="#" class="ml-1" @click.prevent="openCheck(r, 'original', f)">{{ f.original ? '更换正本(교체)' : '上传正本(정본)' }}</a>
                        </span>
                        <template v-for="k in ['check', 'original']">
                          <table v-if="f[k] && cmpOpen[r._id + 'co' + f.no + k]" :key="r._id + 'co' + f.no + k + 'd'" class="table table-sm table-bordered mb-1 mt-1 inv-rec__diff">
                            <thead class="thead-light"><tr><th>项目(항목)</th><th>我们的产地证(우리 서류)</th><th>{{ k === 'check' ? '核对件' : '正本' }}(올린 파일)</th></tr></thead>
                            <tbody>
                              <tr v-for="c in cmpRows(f[k].compare)" :key="c.key" :class="rowClass(c.status)"><td>{{ statusIcon(c.status) }} {{ c.label }}</td><td class="inv-rec__pre">{{ c.expect || '—' }}</td><td class="inv-rec__pre">{{ c.actual || '—' }}</td></tr>
                              <tr v-if="!cmpRows(f[k].compare).length"><td colspan="3" class="text-success">全部一致(모두 일치)</td></tr>
                            </tbody>
                          </table>
                        </template>
                      </div>
                    </td>
                    <td class="small">
                      <template v-if="r.bl">
                        <div><b>{{ r.bl.no }}</b> <a href="#" :class="statusClass(r.bl.compare)" title="点击看差异(눌러서 항목별 차이 보기)" @click.prevent="toggleCmp(r._id + 'bl')">{{ checkText(r.bl.compare) }} {{ cmpOpen[r._id + 'bl'] ? '▲' : '▼' }}</a>
                          <a href="#" class="ml-1" @click.prevent="downloadStored(r.bl.file)">📎</a></div>
                        <!-- 대조 시각·기준 서류 — 箱单发票 를 다시 만들면 저장해 둔 BL 판독값으로 자동 재대조(2026-10-07 YZY) -->
                        <div class="text-muted">核对(대조) {{ fmtDate(r.bl.comparedAt || r.bl.confirmedAt) }}<span v-if="r.bl.basis"> · 基于(기준) {{ fmtDate(r.bl.basis) }} 箱单发票</span></div>
                        <span v-if="blStale(r)" class="badge badge-warning" title="箱单发票已重新生成，核对未更新 — 请重新上传BL(서류를 다시 만들었는데 대조가 갱신되지 않음 — BL 다시 올려 주세요)">核对是旧的(옛 서류 기준 대조)</span>
                        <table v-if="cmpOpen[r._id + 'bl']" class="table table-sm table-bordered mb-1 mt-1 inv-rec__diff">
                          <thead class="thead-light"><tr><th>项目(항목)</th><th>我们的箱单发票(우리 서류)</th><th>提单(BL)</th></tr></thead>
                          <tbody>
                            <tr v-for="c in cmpRows(r.bl.compare)" :key="c.key" :class="rowClass(c.status)"><td>{{ statusIcon(c.status) }} {{ c.label }}</td><td class="inv-rec__pre">{{ c.expect || '—' }}</td><td class="inv-rec__pre">{{ c.actual || '—' }}</td></tr>
                            <tr v-if="!cmpRows(r.bl.compare).length"><td colspan="3" class="text-success">全部一致(모두 일치)</td></tr>
                          </tbody>
                        </table>
                        <div class="text-muted">出库列表BL号 {{ r.bl.sync ? r.bl.sync.선적있음 + '/' + r.bl.sync.선적키 : '' }}<span v-if="r.bl.sync && r.bl.sync.구매요청갱신"> · {{ r.bl.sync.구매요청갱신 }}件</span></div>
                        <!-- 선적 0건 — 이 박스들이 출고리스트로 출고 반영된 적이 없다(BL 은 이 기록에만 남는다) -->
                        <span v-if="r.bl.sync && !r.bl.sync.선적있음" class="badge badge-danger" title="BL只保存在本记录 — 先上传出库清单确认出库(BL 은 이 기록에만 저장 — 출고리스트로 출고 반영 필요)">此BL未关联出库记录(선적 없음)</span>
                      </template>
                      <a href="#" @click.prevent="openCheck(r, 'bl')">{{ r.bl ? '重新上传(다시)' : '上传提单BL(BL 대조)' }}</a>
                    </td>
                    <td class="text-nowrap"><b-button size="sm" variant="outline-primary" :disabled="busyId === r._id" @click="downloadOutbound(r)">⬇ 生成(생성)</b-button>
                      <!-- 이 세트 상품의 KC 인증서류(韩文标签资料 에 올린 것) — 2026-10-07 YZY -->
                      <div class="mt-1"><b-button size="sm" variant="outline-info" class="py-0" :disabled="busyId === r._id" @click="downloadKc(r)">⬇ KC证书(KC 서류)</b-button></div></td>
                    <td class="text-nowrap"><b-button size="sm" variant="link" class="text-danger p-0" :disabled="busyId === r._id" @click="remove(r)">删除</b-button></td>
                  </tr>
                  <tr v-if="!list.length && !loading"><td colspan="9" class="text-center text-muted py-4">没有记录(기록이 없습니다)</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- coupang托盘表 — 当天(오늘 출고) 파렛트만 기본 표시. 누적 기록은 그대로 두고 날짜 선택으로만 이전 것을 본다 -->
    <b-modal v-model="pallet.show" size="xl" scrollable title="coupang托盘表" hide-footer>
      <div class="d-flex align-items-center flex-wrap inv-rec__gap mb-2">
        <label class="mb-0 small">出库日(출고일)</label>
        <select v-model="pallet.date" class="form-control form-control-sm" style="width:150px" @change="loadPallet(pallet.date)">
          <option v-for="d in pallet.dates" :key="d" :value="d">{{ d }}{{ d === pallet.today ? ' (当天)' : '' }}</option>
        </select>
        <b-button size="sm" variant="outline-success" :disabled="!pallet.rows.length" @click="downloadPallet">⬇ Excel</b-button>
        <b-spinner v-if="pallet.loading" small></b-spinner>
        <span v-if="pallet.error" class="small text-danger">{{ pallet.error }}</span>
      </div>
      <div v-if="pallet.rows.length" class="table-responsive">
        <table class="table table-sm table-bordered mb-0 small">
          <thead class="thead-light"><tr><th v-for="(h, i) in pallet.headers" :key="'ph' + i">{{ h }}</th></tr></thead>
          <tbody><tr v-for="(r, ri) in pallet.rows" :key="'pr' + ri"><td v-for="(c, ci) in r" :key="ci" :class="{ 'inv-rec__edit': pallet.editable.includes(ci) }" style="white-space:pre-line"><textarea v-if="pallet.editable.includes(ci)" class="form-control form-control-sm" style="min-width:90px" :rows="Math.max(1, String(c).split('\n').length)" :value="c" @input="editPallet(ri, ci, $event.target.value)"></textarea><template v-else>{{ c }}</template></td></tr></tbody>
        </table>
      </div>
      <div v-else-if="!pallet.loading" class="small text-muted">{{ pallet.date === pallet.today ? '今天还没有出库的托盘(오늘 출고된 파렛트가 아직 없습니다)' : '这一天没有托盘(이 날짜에 파렛트가 없습니다)' }} — 以前的日期请在上面选择(이전 날짜는 위에서 선택)</div>
    </b-modal>

    <!-- 대조 창 — BL 또는 产地证 核对件 -->
    <b-modal v-model="check.show" size="xl" scrollable :title="checkTitle" hide-footer>
      <div class="d-flex align-items-center flex-wrap inv-rec__gap mb-2">
        <input type="file" class="form-control-file" style="width:auto" accept="application/pdf,image/png,image/jpeg" :disabled="check.reading || check.saving" @change="onCheckFile">
        <span v-if="check.reading" class="text-primary small"><b-spinner small></b-spinner> AI 读取中(판독 중, 10~30초)…</span>
        <span v-if="check.readError" class="text-danger small">{{ check.readError }}</span>
      </div>
      <small class="text-muted d-block mb-2">
        {{ check.kind === 'bl'
          ? '与箱单发票核对(인보이스·패킹리스트와 대조) — 收货人·件数·毛重·体积·品名·唛头·船名·港口'
          : '与生成的产地证核对(만든 원산지증명과 대조) — 品名·HS·数量·发票号·收货人·船名·日期' }}
      </small>
      <template v-if="check.compare.length">
        <div class="mb-2">
          <span class="badge badge-success mr-1">一致(일치) {{ checkSum.ok }}</span>
          <span class="badge badge-warning mr-1">请确认(확인 필요) {{ checkSum.warn }}</span>
          <span class="badge badge-danger">不一致(불일치) {{ checkSum.diff }}</span>
        </div>
        <div class="table-responsive inv-rec__cmp">
          <table class="table table-sm table-bordered mb-2 small inv-rec__cards">
            <thead class="thead-light"><tr><th style="width:220px">项目(항목)</th><th>我们的(우리 서류)</th><th>上传文件(올린 파일)</th><th style="width:70px"></th></tr></thead>
            <tbody>
              <tr v-for="c in check.compare" :key="c.key" :class="rowClass(c.status)">
                <td data-label="项目">{{ c.label }}</td>
                <td data-label="我们的" class="inv-rec__pre">{{ c.expect }}</td>
                <td data-label="上传文件" class="inv-rec__pre">{{ c.actual }}</td>
                <td data-label="结果" class="text-center">{{ statusIcon(c.status) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <div v-if="check.file" class="d-flex align-items-center flex-wrap inv-rec__gap border-top pt-2">
        <template v-if="check.kind === 'bl'">
          <label class="mb-0 small font-weight-bold">提单号(BL번호)</label>
          <input v-model.trim="check.blNo" class="form-control form-control-sm" style="width:220px" placeholder="GSPC26064E803A9">
          <small class="text-muted">确认后填入出库列表BL号(AI列)(확인하면 출고리스트 BL번호 칸에 들어갑니다)</small>
        </template>
        <b-button class="ml-auto" size="sm" :variant="checkSum.diff ? 'danger' : 'success'" :disabled="!canConfirm" @click="confirmCheck">
          <b-spinner v-if="check.saving" small></b-spinner>
          {{ checkSum.diff ? '有不一致仍确认保存(불일치 있음 — 그래도 저장)' : '✅ 确认并保存(확인 후 저장)' }}
        </b-button>
      </div>
    </b-modal>

    <!-- 일괄 업로드 — 여러 파일을 AI 로 읽고 행·产地证 을 자동으로 맞춘다. 못 맞춘 파일은 사람이 행을 고른다(2026-10-07 YZY) -->
    <input ref="batchInput" type="file" multiple accept="application/pdf,image/png,image/jpeg" class="d-none" @change="onBatchFiles">
    <b-modal v-model="batch.show" size="xl" scrollable :title="batchTitle" hide-footer :no-close-on-backdrop="batch.running || batch.saving">
      <small class="text-muted d-block mb-2">
        按文件名·内容自动匹配(파일 이름·내용으로 자동 매칭) — {{ batch.kind === 'bl' ? '提单号·唛头(托盘号)·收货人' : '发票号(托盘号 范围 + -1/-2)·收货人·品目' }}。
        没匹配上的请手动选择行(못 맞춘 파일은 행을 직접 골라 주세요)。
      </small>
      <div class="mb-2 small">
        <span class="badge badge-success mr-1">自动匹配(자동) {{ batchSum.auto }}</span>
        <span class="badge badge-warning mr-1">需选择(선택 필요) {{ batchSum.need }}</span>
        <span v-if="batchSum.dup" class="badge badge-danger mr-1">重复(중복) {{ batchSum.dup }}</span>
        <span v-if="batch.running" class="text-primary"><b-spinner small></b-spinner> AI 读取中(판독 중) {{ batchSum.read }}/{{ batch.items.length }}</span>
      </div>
      <div class="table-responsive">
        <table class="table table-sm table-bordered small mb-2 inv-rec__batch">
          <thead class="thead-light">
            <tr>
              <th style="width:30px"></th><th>文件(파일)</th><th style="min-width:300px">匹配行(매칭 행)</th>
              <th>{{ batch.kind === 'bl' ? '提单号(BL번호)' : '产地证' }}</th><th>依据(근거)</th><th>核对(대조)</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="it in batch.items">
              <tr :key="it.key" :class="batchRowClass(it)">
                <td class="text-center"><input v-model="it.include" type="checkbox" :disabled="it.state !== 'done' || !canSaveItem(it)"></td>
                <td class="inv-rec__pre">{{ it.name }}
                  <div v-if="it.state === 'reading'" class="text-primary"><b-spinner small></b-spinner> 读取中(판독 중)</div>
                  <div v-else-if="it.state === 'saved'" class="text-success">✅ 已保存(저장됨)</div>
                  <div v-if="it.error" class="text-danger">{{ it.error }}</div>
                </td>
                <td>
                  <select v-model="it.recordId" class="form-control form-control-sm" :disabled="it.state === 'saved' || batch.saving" @change="onBatchRecord(it)">
                    <option value="">— 请选择行(행 선택) —</option>
                    <option v-for="r in sortedList" :key="r._id" :value="r._id">{{ recordLabel(r) }}</option>
                  </select>
                </td>
                <td class="text-nowrap">
                  <template v-if="batch.kind === 'bl'">
                    <input v-model.trim="it.blNo" class="form-control form-control-sm" style="width:170px" :disabled="it.state === 'saved' || batch.saving">
                  </template>
                  <select v-else v-model.number="it.no" class="form-control form-control-sm" style="width:150px" :disabled="!it.recordId || it.state === 'saved' || batch.saving" @change="recompare(it)">
                    <option :value="null">— CO —</option>
                    <option v-for="f in cooFilesOf(it.recordId)" :key="f.no" :value="f.no">CO{{ f.no }} {{ cooLabel(f.type) }}<template v-if="f.total > 1">-{{ f.fileNo }}</template>{{ f[batch.kind] ? ' ★' : '' }}</option>
                  </select>
                </td>
                <td class="text-nowrap">{{ byText(it) }}<span v-if="isDup(it)" class="badge badge-danger ml-1">重复(중복)</span>
                  <div v-if="replacing(it)" class="text-warning">将替换已有文件(기존 파일 교체)</div>
                </td>
                <td class="text-nowrap">
                  <a v-if="it.compare.length" href="#" :class="statusClass(it.compare)" @click.prevent="it.open = !it.open">{{ checkText(it.compare) }} {{ it.open ? '▲' : '▼' }}</a>
                  <span v-else-if="it.state === 'done'" class="text-muted">—</span>
                </td>
              </tr>
              <tr v-if="it.open && it.compare.length" :key="it.key + 'd'">
                <td colspan="6">
                  <table class="table table-sm table-bordered mb-0 inv-rec__diff">
                    <thead class="thead-light"><tr><th>项目(항목)</th><th>我们的(우리 서류)</th><th>上传文件(올린 파일)</th></tr></thead>
                    <tbody>
                      <tr v-for="c in cmpRows(it.compare)" :key="c.key" :class="rowClass(c.status)"><td>{{ statusIcon(c.status) }} {{ c.label }}</td><td class="inv-rec__pre">{{ c.expect || '—' }}</td><td class="inv-rec__pre">{{ c.actual || '—' }}</td></tr>
                      <tr v-if="!cmpRows(it.compare).length"><td colspan="3" class="text-success">全部一致(모두 일치)</td></tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <div class="d-flex align-items-center inv-rec__gap border-top pt-2">
        <small class="text-muted">不一致的也可以保存，保存后在列表里能看到差异(불일치가 있어도 저장할 수 있고, 목록에서 차이를 볼 수 있습니다)</small>
        <b-button class="ml-auto" size="sm" variant="success" :disabled="batch.running || batch.saving || !batchSaveList.length" @click="saveBatch">
          <b-spinner v-if="batch.saving" small></b-spinner> ✅ 保存选中(선택 저장) {{ batchSaveList.length }}
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { coupassMenuTitle } from './coupassMenu';
import { writeInvoiceXlsx, invoiceFileName, isLooseId } from './invoiceXlsx';
import { COO_NONE_CODE, cooChoice, COO_NOT_NEEDED_TEXT, invoiceAmount, cooNeeded, writeCooXlsx, cooFileName, batchCooFileName, zipFilesUtf8 } from './cooXlsx';
import { writeOutboundDocXlsx, outboundDocFileName, coRefOf, compareBl, compareCoo, compareSummary, nameMatch, outboundListSlot, ownerListsZip, fetchOwnerImages, dropCopyDuplicates } from './invoiceRecord';
import { matchDoc } from './invoiceDocMatch';
import { writeSheetXlsx, palletSheetFileName, sheetRowsForXlsx } from './blSheetXlsx';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const FILE_MAX = 10 * 1024 * 1024;

function b64ToU8(b64) {
  const bin = atob(b64);
  const u8 = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
  return u8;
}
function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(new Error('文件读取失败(파일을 읽지 못했습니다)'));
    reader.readAsDataURL(file);
  });
}
// 이미지는 jpg 로 올라와도 브라우저가 붙인 type 이 비는 경우가 있어 확장자로 보충한다
function fileType(file) {
  if (file.type) return file.type === 'image/jpg' ? 'image/jpeg' : file.type;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return { pdf: 'application/pdf', png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg' }[ext] || '';
}
// 보관 파일 확장자 — 이름에 있으면 그것, 없으면 type 으로
function storedExt(meta) {
  const m = /\.([A-Za-z0-9]{2,5})$/.exec(String((meta && meta.name) || ''));
  if (m) return '.' + m[1].toLowerCase();
  return { 'application/pdf': '.pdf', 'image/png': '.png', 'image/jpeg': '.jpg' }[meta && meta.type] || '';
}
const BATCH_READ_PARALLEL = 3; // AI 판독 동시 개수(한 장 10~30초)

export default {
  name: 'InvoiceRecordList',
  // embedded — 出库列表(warehouseManagement) 의 「生成箱单发票」 옆 탭으로 들어갈 때(2026-10-04 YZY 「挪到 生成箱单发票 旁边」)
  props: { embedded: { type: Boolean, default: false } },
  data() {
    return {
      pageTitle: coupassMenuTitle('logis.invoicerecords'),
      cooNotNeededText: COO_NOT_NEEDED_TEXT,
      cooNoneCode: COO_NONE_CODE,
      pallet: { show: false, date: '', today: '', dates: [], headers: [], rows: [], editable: [], loading: false, error: '' },
      companyid: '',
      showReplaced: false, // 覆盖(덮어쓰기)로 숨긴 옛 기록도 보기
      list: [],
      loading: false,
      error: '',
      busyId: '',
      cmpOpen: {},
      check: this.emptyCheck(),
      batch: { show: false, kind: '', items: [], running: false, saving: false },
      batchKind: '',
      dlTypes: [
        { key: 'invoice', label: '箱单发票' },
        { key: 'bl', label: '提单' },
        { key: 'outbound', label: '出库资料文件' },
        { key: 'original', label: '产地证正本' },
        // 세트 상품(박스 바코드) 중 韩文标签资料 에 KC 인증서류를 올린 것(2026-10-07 YZY)
        { key: 'kc', label: 'KC证书' }
      ],
      dl: { date: '', types: ['invoice', 'bl', 'outbound', 'original', 'kc'], busy: false, progress: '', ownerBusy: false }
    };
  },
  computed: {
    companyOptions() { return [...new Set(this.list.map(r => r.companyid))].sort(); },
    dateCount() {
      const m = {};
      this.list.forEach(r => { const d = this.dateOf(r); if (d) m[d] = (m[d] || 0) + 1; });
      return m;
    },
    dateOptions() { return Object.keys(this.dateCount).sort().reverse(); },
    recordMap() {
      const m = {};
      this.list.forEach(r => { m[r._id] = r; });
      return m;
    },
    batchTitle() {
      return { check: '批量上传产地证核对件(원산지증명 대조 일괄)', original: '批量上传产地证正本(정본 일괄)', bl: '批量上传提单BL(BL 일괄)' }[this.batch.kind] || '';
    },
    // 같은 행·같은 产地证(BL 은 같은 행)에 파일이 둘 이상 붙으면 중복
    batchDupKeys() {
      const n = {};
      this.batch.items.forEach(it => { const k = this.batchTarget(it); if (k) n[k] = (n[k] || 0) + 1; });
      return Object.keys(n).filter(k => n[k] > 1);
    },
    batchSum() {
      const its = this.batch.items;
      return {
        read: its.filter(it => it.state !== 'reading' && it.state !== 'wait').length,
        auto: its.filter(it => it.by && it.recordId === it.autoId).length,
        need: its.filter(it => it.state !== 'reading' && it.state !== 'wait' && it.state !== 'saved' && !this.canSaveItem(it)).length,
        dup: its.filter(it => this.isDup(it)).length
      };
    },
    batchSaveList() { return this.batch.items.filter(it => it.include && it.state === 'done' && this.canSaveItem(it) && !this.isDup(it)); },
    // #N — 그날 方数计划 파일에서 托盘 이 처음 나오는 행(planPos, 서버가 붙인다) 순서. 같은 행이면 같은 번호(2026-10-07 YZY)
    planRank() {
      const byDate = {};
      this.list.forEach(r => { if (Number.isInteger(r.planPos)) (byDate[this.dateOf(r)] = byDate[this.dateOf(r)] || new Set()).add(r.planPos); });
      const rank = {};
      Object.keys(byDate).forEach(d => { byDate[d] = [...byDate[d]].sort((x, y) => x - y); });
      this.list.forEach(r => { if (Number.isInteger(r.planPos)) rank[r._id] = byDate[this.dateOf(r)].indexOf(r.planPos) + 1; });
      return rank;
    },
    // 方数计划 날짜 최근 먼저 → 方数计划 파일 순서(#N) → 순번(일괄 생성) → 순번 없는 것은 최근 저장 먼저
    sortedList() {
      return this.list.slice().sort((a, b) => {
        const da = this.dateOf(a);
        const db = this.dateOf(b);
        if (da !== db) return da < db ? 1 : -1;
        const pa = Number.isInteger(a.planPos) ? a.planPos : Infinity;
        const pb = Number.isInteger(b.planPos) ? b.planPos : Infinity;
        if (pa !== pb) return pa < pb ? -1 : 1;
        const sa = a.seq || Infinity;
        const sb = b.seq || Infinity;
        if (sa !== sb) return sa < sb ? -1 : 1;
        return String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''));
      });
    },
    checkSum() { return compareSummary(this.check.compare); },
    checkTitle() {
      const r = this.check.record;
      if (!r) return '';
      const head = (r.palletIds || []).join(', ') + ' (' + r.companyid + ')';
      if (this.check.kind === 'bl') return '上传提单BL·核对(BL 대조) — ' + head;
      const co = 'CO' + (this.check.coo && this.check.coo.no) + ' · ' + head;
      return this.check.kind === 'original' ? '上传产地证正本·核对(정본 대조) — ' + co : '上传产地证核对件(원산지증명 대조) — ' + co;
    },
    canConfirm() {
      if (!this.check.file || this.check.reading || this.check.saving) return false;
      return this.check.kind !== 'bl' || /^[A-Za-z0-9-]{6,40}$/.test(this.check.blNo);
    }
  },
  mounted() {
    if (!this.embedded) document.title = this.pageTitle;
    this.load();
  },
  methods: {
    // coupang托盘表 — 날짜를 안 주면 서버가 오늘(한국 날짜)을 준다 = 当天
    openPallet() { this.pallet.show = true; this.loadPallet(''); },
    async loadPallet(date) {
      const pl = this.pallet;
      pl.loading = true;
      pl.error = '';
      try {
        const { data } = await axios.post('/api/coupassCustoms/palletSheet', { date: date || undefined });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '失败(실패)');
        if (!date) pl.today = data.date;
        pl.date = data.date;
        pl.dates = [...new Set([pl.today, data.date].filter(Boolean).concat(data.dates))].sort().reverse();
        pl.headers = data.headers;
        pl.rows = data.rows;
        pl.editable = data.editable || [];
      } catch (e) {
        console.error('托盘表 불러오기 실패', e);
        pl.error = e.message;
      } finally {
        pl.loading = false;
      }
    },
    // 분류 칸이 비어 있으면 직원이 화면에서 채운다 — 저장하지 않고 화면·엑셀에만 반영
    editPallet(ri, ci, v) { this.$set(this.pallet.rows[ri], ci, v); },
    downloadPallet() { writeSheetXlsx(this.pallet.headers, sheetRowsForXlsx(this.pallet.rows, this.pallet.editable), 'Sheet1', palletSheetFileName(this.pallet.date)); },
    emptyCheck() {
      return { show: false, kind: '', record: null, coo: null, file: null, reading: false, saving: false, readError: '', extracted: null, compare: [], blNo: '' };
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/coupassInvoiceRecord/list', { companyid: this.companyid, showReplaced: this.showReplaced });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '查询失败');
        this.list = data.list || [];
        if (!this.dateOptions.includes(this.dl.date)) this.dl.date = this.dateOptions[0] || '';
      } catch (e) {
        console.error('箱单发票列表 조회 실패:', e);
        this.error = '查询失败(조회 실패): ' + e.message;
      } finally {
        this.loading = false;
      }
    },
    async getRecord(id) {
      const { data } = await axios.post('/api/coupassInvoiceRecord/get', { id });
      if (data.요청결과 !== '성공' || !data.record) throw new Error((data.error && data.error.errorMessage) || '没有记录(기록 없음)');
      return data.record;
    },
    fmtDate(v) {
      if (!v) return '';
      const d = new Date(v);
      return isNaN(d) ? '' : d.toLocaleString();
    },
    num2(v) { return (Number(v) || 0).toFixed(2); },
    dateOf(r) { return r.planDate || (r.info && r.info.date) || ''; },
    // 날짜 묶음의 첫 행·끝 행 — 굵은 테두리(sortedList 가 날짜순이라 앞뒤 행만 보면 된다)
    dayEdgeClass(i) {
      const l = this.sortedList;
      const d = this.dateOf(l[i]);
      return {
        'inv-rec__day--first': i === 0 || this.dateOf(l[i - 1]) !== d,
        'inv-rec__day--last': i === l.length - 1 || this.dateOf(l[i + 1]) !== d
      };
    },
    // 方数计划 에 없는 기록만 쓰는 옛 번호 설명
    seqTip() {
      return '带*：方数计划里没有这些托盘 — 这是批量生成的序号(与文件名里的序号相同)'
        + '(* 표시 = 방수계획에 없는 托盘 — 일괄 생성 순번, 파일명 순번과 같음)';
    },
    planTip(r) {
      return '按当天方数计划文件里托盘出现的顺序排列，这是第' + this.planRank[r._id] + '单(#' + this.planRank[r._id] + ')'
        + '(그날 방수계획 파일 순서 ' + this.planRank[r._id] + '번째)';
    },
    // 单独清关 화주 합치기 세트 — 원래 화주들(pallets.origCompanyids, 2026-10-07 YZY). 综合清关 은 원래 많아서 안 보인다
    mergedOf(r) {
      if (r.clearance === 'integrated') return [];
      const s = new Set();
      (r.pallets || []).forEach(p => (p.origCompanyids || []).forEach(c => s.add(c)));
      return s.size > 1 || (s.size === 1 && !s.has(r.companyid)) ? [...s] : [];
    },
    importerName(r) { return String((r.info && r.info.importer && r.info.importer.name) || '').trim(); },
    // 托盘号 한 줄씩 — 散货 는 「RUBY0930-散货 [FU092821, FU092822…]」
    palletLines(r) {
      const ps = r.pallets && r.pallets.length ? r.pallets : (r.palletIds || []).map(id => ({ id }));
      return ps.map(p => {
        const codes = isLooseId(p.id) ? (p.boxes || []).map(b => b.code).filter(Boolean) : [];
        return codes.length ? p.id + ' [' + codes.join(', ') + ']' : p.id;
      });
    },
    // 箱单发票 총금액(USD) < 230 이면 「无需做产地证」. 금액 = 저장된 summary.amount(옛 기록은 pallets 로 다시 셈).
    // 기준 생기기 전에 이미 만든 产地证 은 지우지 않고 아래에 그대로 보인다
    cooNotNeeded(r) {
      if (r.summary && r.summary.cooNone) return true; // 「无需做产地证」를 직접 고른 건(2026-10-08)
      const amount = r.summary && r.summary.amount !== undefined ? r.summary.amount : invoiceAmount(r);
      return !cooNeeded(amount);
    },
    cooLabel(type) { const t = cooChoice(type); return t ? t.label : type; },
    // 产地证 종류별 색(InvoiceGenerator 와 같은 값, 2026-10-08 YZY)
    cooStyle(type) { const t = cooChoice(type); return t ? { color: t.color, backgroundColor: t.bg, fontWeight: 'bold' } : {}; },
    statusIcon(s) { return { ok: '✅', warn: '⚠️', diff: '❌', info: '—' }[s] || ''; },
    rowClass(s) { return { warn: 'table-warning', diff: 'table-danger' }[s] || ''; },
    checkText(list) {
      const s = compareSummary(list);
      if (!list || !list.length) return '已确认(확인)';
      return s.diff ? '不一致 ' + s.diff : (s.warn ? '请确认 ' + s.warn : '一致');
    },
    // 다른 항목만(불일치·확인 필요) — 배지를 누르면 펼친다
    cmpRows(list) { return (list || []).filter(c => c.status === 'diff' || c.status === 'warn'); },
    toggleCmp(k) { this.$set(this.cmpOpen, k, !this.cmpOpen[k]); },
    // 서류를 다시 저장한 뒤 BL 대조가 따라오지 못했다(savedAt 이 있는 기록만 — 옛 기록은 다음 저장 때부터)
    blStale(r) {
      if (!r.bl || !r.savedAt || !r.bl.extracted) return false;
      const basis = r.bl.basis ? new Date(r.bl.basis).getTime() : new Date(r.bl.confirmedAt || 0).getTime();
      return new Date(r.savedAt).getTime() > basis;
    },
    statusClass(list) {
      const s = compareSummary(list);
      return s.diff ? 'text-danger' : (s.warn ? 'text-warning' : 'text-success');
    },
    saveFile(data, name, type) {
      const blob = new Blob([data], { type });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
    },
    // 화주별 출고리스트 — 그날 저장된 출고리스트(出库列表 업로드·生成箱单发票 업로드 둘 다 같은 저장소) 를 综合/单独 칸으로 가른 뒤 화주마다 한 파일, zip 하나.
    // 칸 = 生成箱单发票 저장본 칸 기본값과 같은 규칙(outboundListSlot), 파일 이름·모양은 예전 버튼과 같다(invoiceRecord.js ownerListsZip)
    async downloadOwnerLists() {
      const date = this.dl.date;
      if (!date || this.dl.ownerBusy) return;
      this.dl.ownerBusy = true;
      this.error = '';
      try {
        const { data } = await axios.post('/api/coupassOutboundList/get', { date, aoa: true });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '读取失败');
        const slots = { integrated: [], separate: [] };
        dropCopyDuplicates((data.files || []).filter(f => Array.isArray(f.aoa) && f.aoa.length > 1))
          .forEach(f => slots[outboundListSlot(f)].push({ name: f.fileName, aoa: f.aoa }));
        const imgMap = await fetchOwnerImages([].concat(slots.integrated, slots.separate));
        const out = ownerListsZip(slots, date, imgMap);
        if (!out) {
          alert(date + ' 没有保存的出库清单(저장된 출고리스트 없음) — 请先在「出库列表」或「生成箱单发票」上传(출고리스트를 먼저 올려 주세요)');
          return;
        }
        this.saveFile(out.data, out.name, 'application/zip');
      } catch (e) {
        console.error('화주별 출고리스트 생성 실패:', e);
        this.error = '按货主拆分失败(화주별 출고리스트 실패): ' + e.message;
      } finally {
        this.dl.ownerBusy = false;
      }
    },
    modelOf(r) { return { companyids: r.companyids || [r.companyid], pallets: r.pallets || [], warnings: [] }; },
    // id 가 있으면 그 장(箱单发票 만들 때 고른 营业执照), 없으면 예전처럼 화주의 첫 장
    async loadImage(kind, companyid, id) {
      try {
        const { data } = await axios.post('/api/coupassInvoiceImage/get', id ? { id } : { kind, companyid });
        const doc = data.요청결과 === '성공' && data.image;
        return doc && doc.data ? { name: doc.name, type: doc.type, data: b64ToU8(doc.data), width: doc.width, height: doc.height } : null;
      } catch (e) {
        console.error('箱单发票 이미지 읽기 실패:', e);
        return null;
      }
    },
    // 기록으로 箱单发票 를 다시 만든다 — 产地证 을 만들었으면 H 열(CO 번호)이 채워진다
    // 기록 → 箱单发票 { name, data }. imgCache — 날짜 일괄 다운로드가 같은 도장·营业执照 를 한 번만 읽게
    async invoiceOut(r, imgCache) {
      const c = imgCache || {};
      const get = (kind, co, id) => {
        const k = kind + '|' + (id || co);
        if (!c[k]) c[k] = this.loadImage(kind, co, id);
        return c[k];
      };
      const [seal, license] = await Promise.all([get('seal', '', r.info && r.info.sealId), get('license', r.companyid, r.info && r.info.licenseId)]);
      const files = (r.coo && r.coo.files) || [];
      const data = writeInvoiceXlsx(this.modelOf(r), r.info, { seal, license }, coRefOf(files));
      return { name: r.docName ? 'INVOICE_' + r.docName + '.xlsx' : invoiceFileName(r.info, this.modelOf(r)), data };
    },
    async downloadInvoice(r) {
      this.busyId = r._id;
      try {
        const out = await this.invoiceOut(r);
        this.saveFile(out.data, out.name, XLSX_MIME);
      } catch (e) {
        console.error('箱单发票 다시 만들기 실패:', e);
        alert('生成失败(생성 실패): ' + e.message);
      } finally {
        this.busyId = '';
      }
    },
    // 기록의 产地证 한 장 → { name, data }
    cooOut(r, f) {
      const file = { type: f.type, no: f.fileNo, total: f.total, items: f.items, seq: f.no, count: r.coo.files.length }; // seq·count = 하단 TOTAL 장별 배분
      const data = writeCooXlsx(file, this.modelOf(r), r.info, (r.coo && r.coo.info) || {});
      return { name: r.docName ? batchCooFileName(file, r.docName) : cooFileName(file, r.info, this.modelOf(r)), data };
    },
    downloadCoo(r, f) {
      try {
        const out = this.cooOut(r, f);
        this.saveFile(out.data, out.name, XLSX_MIME);
      } catch (e) {
        console.error('产地证 다시 만들기 실패:', e);
        alert('生成失败(생성 실패): ' + e.message);
      }
    },
    async downloadOutbound(r) {
      this.busyId = r._id;
      try {
        const full = await this.getRecord(r._id);
        if (!full.outRows || !full.outRows.length) throw new Error('没有出库行(출고 행이 없습니다) — 请重新生成箱单发票(箱单发票 를 다시 만들어 주세요)');
        const out = writeOutboundDocXlsx(full.outHeader, full.outRows);
        this.saveFile(out, outboundDocFileName(full), XLSX_MIME);
      } catch (e) {
        console.error('출고자료 생성 실패:', e);
        alert('生成失败(생성 실패): ' + e.message);
      } finally {
        this.busyId = '';
      }
    },
    // 보관 파일(BL·核对件·正本) 바이트 → { name, type, data(Uint8Array) }
    async fetchStored(meta) {
      const { data } = await axios.post('/api/coupassInvoiceRecord/file', { fileId: meta.fileId });
      if (data.요청결과 !== '성공' || !data.file) throw new Error((data.error && data.error.errorMessage) || '没有文件(파일 없음)');
      return { name: data.file.name || 'file', type: data.file.type, data: b64ToU8(data.file.data) };
    },
    async downloadStored(meta) {
      if (!meta || !meta.fileId) return;
      try {
        const f = await this.fetchStored(meta);
        this.saveFile(f.data, f.name, f.type);
      } catch (e) {
        console.error('보관 파일 받기 실패:', e);
        alert('下载失败(다운로드 실패): ' + e.message);
      }
    },
    openCheck(r, kind, f) {
      this.check = Object.assign(this.emptyCheck(), { show: true, kind, record: r, coo: f || null });
    },
    async onCheckFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (!file) return;
      const type = fileType(file);
      if (!['application/pdf', 'image/png', 'image/jpeg'].includes(type)) { alert('只能上传PDF·图片(PDF·이미지만 됩니다)'); return; }
      if (file.size > FILE_MAX) { alert('文件太大(10MB 이하만 됩니다)'); return; }
      const c = this.check;
      Object.assign(c, { file: null, reading: true, readError: '', extracted: null, compare: [], blNo: '' });
      try {
        const data64 = await fileToB64(file);
        c.file = { name: file.name, type, data: data64 };
        const [record, res] = await Promise.all([
          this.getRecord(c.record._id),
          axios.post('/api/coupassInvoiceRecord/readDoc', { kind: c.kind === 'bl' ? 'bl' : 'coo', file: c.file })
        ]);
        c.record = record;
        if (c.coo) c.coo = ((record.coo && record.coo.files) || []).find(f => f.no === c.coo.no) || c.coo;
        const data = res.data;
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || 'AI 判读失败');
        c.extracted = data.result || {};
        if (c.kind === 'bl') {
          c.compare = compareBl(record, c.extracted);
          c.blNo = String(c.extracted.blNo || '').replace(/\s/g, '').toUpperCase();
        } else {
          c.compare = compareCoo(record, c.coo, c.extracted);
        }
      } catch (err) {
        console.error('서류 대조 실패:', err);
        // 판독이 안 돼도 파일은 남길 수 있게 한다 — 사람이 눈으로 보고 저장(BL 번호는 직접 입력)
        c.readError = '读取失败，可人工核对后保存(판독 실패 — 직접 확인 후 저장할 수 있습니다): ' + err.message;
      } finally {
        c.reading = false;
      }
    },
    async confirmCheck() {
      const c = this.check;
      if (this.checkSum.diff && !confirm('有不一致的项目，确定保存吗?(불일치 항목이 있습니다. 그래도 저장할까요?)')) return;
      if (!c.compare.length && !confirm('没有核对结果，确定保存吗?(대조 결과 없이 저장할까요?)')) return;
      c.saving = true;
      try {
        const body = c.kind === 'bl'
          ? { id: c.record._id, blNo: c.blNo, file: c.file, compare: c.compare, extracted: c.extracted }
          : { id: c.record._id, kind: c.kind === 'original' ? 'original' : 'check', no: c.coo.no, file: c.file, compare: c.compare, extracted: c.extracted };
        const { data } = await axios.post(c.kind === 'bl' ? '/api/coupassInvoiceRecord/confirmBl' : '/api/coupassInvoiceRecord/cooFile', body);
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '保存失败');
        if (c.kind === 'bl' && data.bl && data.bl.sync) {
          const s = data.bl.sync;
          // 선적 0건 = 이 박스들은 출고리스트로 출고 반영된 적이 없다 — BL 은 기록에만 남는다(빨간 경고)
          const none = !s.선적있음;
          this.$bvToast.toast(none
            ? '此BL未关联出库记录(선적 없음) — 箱子 ' + s.선적키 + '个 都没有出库记录, BL只保存在本记录(이 기록에만 저장)'
            : '出库列表BL号(선적) ' + s.선적있음 + '件 · 箱子(박스) ' + s.선적키 + '个',
          { title: (none ? '⚠ BL ' : '✅ BL ') + data.bl.no, variant: none ? 'danger' : 'success', solid: true, autoHideDelay: none ? 10000 : 6000 });
        }
        c.show = false;
        await this.load();
      } catch (e) {
        console.error('대조 결과 저장 실패:', e);
        alert('保存失败(저장 실패): ' + e.message);
      } finally {
        c.saving = false;
      }
    },
    // ───── 일괄 업로드(核对件·正本·提单) ─────
    recordLabel(r) {
      return this.dateOf(r) + ' · ' + (r.docName || (r.companyid + ' (' + (r.palletIds || []).join(', ') + ')'));
    },
    cooFilesOf(id) {
      const r = this.recordMap[id];
      return (r && r.coo && r.coo.files) || [];
    },
    batchTarget(it) {
      if (!it.recordId || it.state === 'saved') return '';
      return this.batch.kind === 'bl' ? it.recordId : (it.no ? it.recordId + '#' + it.no : '');
    },
    isDup(it) {
      const k = this.batchTarget(it);
      return !!k && this.batchDupKeys.includes(k);
    },
    canSaveItem(it) {
      if (!it.file || !it.recordId || !this.recordMap[it.recordId]) return false;
      if (this.batch.kind === 'bl') return /^[A-Za-z0-9-]{6,40}$/.test(it.blNo || '');
      return !!it.no && this.cooFilesOf(it.recordId).some(f => f.no === it.no);
    },
    // 이미 올린 파일이 있는 자리 — 저장하면 새 파일로 바뀐다(옛 파일은 보관 그대로)
    replacing(it) {
      const r = this.recordMap[it.recordId];
      if (!r || it.state === 'saved') return false;
      if (this.batch.kind === 'bl') return !!r.bl;
      const f = this.cooFilesOf(it.recordId).find(x => x.no === it.no);
      return !!(f && f[this.batch.kind]);
    },
    byText(it) {
      if (it.state === 'wait' || it.state === 'reading') return '';
      if (!it.recordId) return it.cands > 1 ? '候选 ' + it.cands + ' 个，请选择(후보 ' + it.cands + '개 — 선택)' : '未匹配(못 맞춤)';
      if (it.recordId !== it.autoId) return '手动(수동 선택)';
      return { invoiceNo: '发票号一致(송장번호)', blNo: '提单号一致(BL번호)', pallet: '托盘号·收货人(파렛트 번호)' }[it.by] || '';
    },
    batchRowClass(it) {
      if (it.state === 'saved') return 'table-success';
      if (this.isDup(it)) return 'table-danger';
      if (it.state === 'done' && !this.canSaveItem(it)) return 'table-warning';
      return '';
    },
    pickBatch(kind) {
      this.batchKind = kind;
      const el = this.$refs.batchInput;
      el.value = '';
      el.click();
    },
    onBatchFiles(e) {
      const files = Array.from((e.target && e.target.files) || []);
      e.target.value = '';
      if (!files.length) return;
      const items = files.map((file, i) => {
        const type = fileType(file);
        const bad = !['application/pdf', 'image/png', 'image/jpeg'].includes(type) ? '只能上传PDF·图片(PDF·이미지만 됩니다)'
          : (file.size > FILE_MAX ? '文件太大(10MB 이하만 됩니다)' : '');
        return {
          key: i + ':' + file.name, name: file.name, raw: file, type, state: bad ? 'error' : 'wait', error: bad,
          file: null, extracted: null, recordId: '', autoId: '', no: null, by: '', cands: 0, blNo: '', compare: [], include: false, open: false
        };
      });
      this.batch = { show: true, kind: this.batchKind, items, running: true, saving: false };
      this.runBatchReads();
    },
    async runBatchReads() {
      const queue = this.batch.items.filter(it => it.state === 'wait');
      const worker = async () => {
        while (queue.length) await this.readBatchItem(queue.shift());
      };
      try {
        await Promise.all(Array.from({ length: BATCH_READ_PARALLEL }, worker));
      } finally {
        this.batch.running = false;
      }
    },
    matchOpts() {
      return {
        consigneeOk: (r, cons) => nameMatch(this.importerName(r), cons),
        cooScore: (r, f, x) => compareSummary(compareCoo(r, f, x)).diff
      };
    },
    // 한 파일 — AI 판독 → 행·产地证 맞추기 → 대조. 판독이 실패해도 파일 이름으로 맞춰 보고, 사람이 보고 저장할 수 있다
    async readBatchItem(it) {
      const kind = this.batch.kind;
      it.state = 'reading';
      try {
        it.file = { name: it.name, type: it.type, data: await fileToB64(it.raw) };
        try {
          const { data } = await axios.post('/api/coupassInvoiceRecord/readDoc', { kind: kind === 'bl' ? 'bl' : 'coo', file: it.file });
          if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || 'AI 判读失败');
          it.extracted = data.result || {};
        } catch (err) {
          console.error('일괄 업로드 판독 실패:', it.name, err);
          it.error = '读取失败，按文件名匹配(판독 실패 — 파일 이름으로만 맞춤): ' + err.message;
        }
        const m = matchDoc(kind === 'bl' ? 'bl' : 'coo', this.list, { name: it.name, extracted: it.extracted }, this.matchOpts());
        Object.assign(it, { recordId: m.recordId, autoId: m.recordId, no: m.no, by: m.by, cands: m.candidates.length });
        if (kind === 'bl') {
          const fromName = /[A-Z]{4}\d[A-Z0-9]{6,}/.exec(it.name.toUpperCase());
          it.blNo = String((it.extracted && it.extracted.blNo) || (fromName && fromName[0]) || '').replace(/\s/g, '').toUpperCase();
        }
        this.recompare(it);
        it.include = this.canSaveItem(it);
        it.state = 'done';
      } catch (err) {
        console.error('일괄 업로드 파일 처리 실패:', it.name, err);
        it.error = '处理失败(처리 실패): ' + err.message;
        it.state = 'error';
      }
    },
    recompare(it) {
      const r = this.recordMap[it.recordId];
      if (!r || !it.extracted) { it.compare = []; return; }
      if (this.batch.kind === 'bl') { it.compare = compareBl(r, it.extracted); return; }
      const f = this.cooFilesOf(it.recordId).find(x => x.no === it.no);
      it.compare = f ? compareCoo(r, f, it.extracted) : [];
    },
    // 사람이 행을 바꿨다 — 产地证 한 장이면 그것, 여러 장이면 품목 대조가 가장 잘 맞는 장
    onBatchRecord(it) {
      if (this.batch.kind !== 'bl') {
        const files = this.cooFilesOf(it.recordId);
        let no = files.length === 1 ? files[0].no : null;
        if (files.length > 1 && it.extracted) {
          const s = files.map(f => ({ no: f.no, d: compareSummary(compareCoo(this.recordMap[it.recordId], f, it.extracted)).diff })).sort((a, b) => a.d - b.d);
          if (s[0].d < s[1].d) no = s[0].no;
        }
        it.no = no;
      }
      this.recompare(it);
      it.include = this.canSaveItem(it);
    },
    async saveBatch() {
      const list = this.batchSaveList.slice();
      if (!list.length) return;
      const diff = list.filter(it => compareSummary(it.compare).diff).length;
      if (diff && !confirm(diff + ' 个文件有不一致，确定保存吗?(' + diff + '개 파일에 불일치가 있습니다. 그래도 저장할까요?)')) return;
      const kind = this.batch.kind;
      this.batch.saving = true;
      let ok = 0;
      const noShip = [];
      try {
        for (const it of list) {
          try {
            const body = kind === 'bl'
              ? { id: it.recordId, blNo: it.blNo, file: it.file, compare: it.compare, extracted: it.extracted }
              : { id: it.recordId, kind, no: it.no, file: it.file, compare: it.compare, extracted: it.extracted };
            const { data } = await axios.post(kind === 'bl' ? '/api/coupassInvoiceRecord/confirmBl' : '/api/coupassInvoiceRecord/cooFile', body);
            if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '保存失败');
            if (kind === 'bl' && data.bl && data.bl.sync && !data.bl.sync.선적있음) noShip.push(data.bl.no);
            Object.assign(it, { state: 'saved', include: false, error: '', file: null });
            ok++;
          } catch (e) {
            console.error('일괄 업로드 저장 실패:', it.name, e);
            it.error = '保存失败(저장 실패): ' + e.message;
          }
        }
        await this.load();
      } finally {
        this.batch.saving = false;
      }
      this.$bvToast.toast('已保存(저장) ' + ok + '/' + list.length + (noShip.length ? ' · 此BL未关联出库记录(선적 없음): ' + noShip.join(', ') : ''),
        { title: noShip.length || ok < list.length ? '⚠ 批量上传(일괄 업로드)' : '✅ 批量上传(일괄 업로드)', variant: noShip.length || ok < list.length ? 'warning' : 'success', solid: true, autoHideDelay: 8000 });
    },
    // ───── KC证书 ─────
    // 세트(기록)의 박스 바코드 → 韩文标签资料 에 올린 KC 인증서류. 기록 목록에는 boxes 가 없어 한 건을 다시 읽는다
    async kcFilesOf(r) {
      const full = await this.getRecord(r._id);
      const seen = {};
      const pairs = [];
      (full.boxes || []).forEach(b => {
        const companyid = b.companyid || full.companyid;
        const barcode = String(b.바코드 || '').replace(/\s+/g, '');
        const k = companyid + '|' + barcode;
        if (!companyid || !barcode || seen[k]) return;
        seen[k] = 1;
        pairs.push({ companyid, barcode });
      });
      if (!pairs.length) return [];
      const { data } = await axios.post('/api/coupassLabel/kcFilesFor', { pairs });
      if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || 'KC证书查询失败');
      const out = [];
      for (const l of data.list || []) {
        for (const meta of l.kcFiles || []) {
          const f = await this.fetchStored(meta);
          out.push({ name: 'KC证书_' + l.barcode + '_' + f.name, type: f.type, data: f.data });
        }
      }
      return out;
    },
    async downloadKc(r) {
      this.busyId = r._id;
      try {
        const files = await this.kcFilesOf(r);
        if (!files.length) { alert('该套商品没有KC证书(이 세트 상품에 올린 KC 인증서류가 없습니다 — 韩文标签资料 에서 올립니다)'); return; }
        if (files.length === 1) this.saveFile(files[0].data, files[0].name, files[0].type);
        else this.saveFile(zipFilesUtf8(files.map(f => ({ name: f.name, data: f.data }))), 'KC证书_' + this.recordDir(r) + '.zip', 'application/zip');
      } catch (e) {
        console.error('KC 인증서류 받기 실패:', e);
        alert('下载失败(다운로드 실패): ' + e.message);
      } finally {
        this.busyId = '';
      }
    },
    // ───── 날짜 일괄 다운로드 ─────
    // zip 안 폴더 = 세트(행) 하나 — 순번 파일명(「INFOREST-20260930-6(INFO0930-P01~INFO0930-P03)」), 옛 기록은 화주(托盘号)
    recordDir(r) {
      if (r.docName) return r.docName;
      const ids = r.palletIds || [];
      return r.companyid + '(' + (ids.length > 1 ? ids[0] + '~' + ids[ids.length - 1] : ids[0] || '') + ')';
    },
    async downloadDay() {
      const d = this.dl.date;
      const types = this.dl.types;
      const recs = this.sortedList.filter(r => this.dateOf(r) === d && !r.replacedBy); // 덮어쓴 옛 기록은 묶음 다운로드에서 뺀다
      if (!recs.length) return;
      this.dl.busy = true;
      const out = [];
      const miss = [];
      const imgCache = {};
      try {
        for (let i = 0; i < recs.length; i++) {
          const r = recs[i];
          const dir = this.recordDir(r);
          this.dl.progress = (i + 1) + '/' + recs.length + ' ' + dir;
          try {
            if (types.includes('invoice')) {
              const f = await this.invoiceOut(r, imgCache);
              out.push({ dir, name: f.name, data: f.data });
            }
            if (types.includes('bl')) {
              if (r.bl && r.bl.file && r.bl.file.fileId) {
                const f = await this.fetchStored(r.bl.file);
                out.push({ dir, name: '提单_' + r.bl.no + storedExt(f), data: f.data });
              } else miss.push(dir + ' — 提单');
            }
            if (types.includes('outbound')) {
              const full = await this.getRecord(r._id);
              // 폴더가 이미 세트라 이름은 짧게(낱개 다운로드 이름은 托盘号 를 다 늘어놓아 길다)
              if (full.outRows && full.outRows.length) out.push({ dir, name: '出库资料文件' + (full.bl && full.bl.no ? '_' + full.bl.no : '') + '.xlsx', data: writeOutboundDocXlsx(full.outHeader, full.outRows) });
              else miss.push(dir + ' — 出库资料文件');
            }
            if (types.includes('kc')) {
              const kcs = await this.kcFilesOf(r);
              // KC 서류가 없는 세트는 「빠진 파일」에 넣지 않는다 — KC 대상이 아닌 세트가 대부분이다
              for (const k of kcs) out.push({ dir, name: k.name, data: k.data });
            }
            if (types.includes('original')) {
              const cos = (r.coo && r.coo.files) || [];
              if (!cos.length && !this.cooNotNeeded(r)) miss.push(dir + ' — 产地证(未生成)');
              for (const co of cos) {
                const meta = co.original && co.original.file;
                if (meta && meta.fileId) {
                  const f = await this.fetchStored(meta);
                  out.push({ dir, name: '产地证正本_CO' + co.no + '_' + co.type + (co.total > 1 ? '-' + co.fileNo : '') + storedExt(f), data: f.data });
                } else miss.push(dir + ' — 产地证正本 CO' + co.no);
              }
            }
          } catch (e) {
            console.error('날짜 일괄 다운로드 실패:', dir, e);
            miss.push(dir + ' — 失败(실패): ' + e.message);
          }
        }
        if (!out.length) { alert('没有可下载的文件(받을 파일이 없습니다)\n' + miss.join('\n')); return; }
        if (miss.length) out.push({ name: '缺少的文件(빠진 파일).txt', data: new TextEncoder().encode(miss.join('\r\n')) });
        this.saveFile(zipFilesUtf8(out), '箱单发票资料_' + d + '（' + recs.length + '套）.zip', 'application/zip');
        if (miss.length) {
          this.$bvToast.toast('缺少(빠짐) ' + miss.length + ' — zip 里有清单(zip 안 「缺少的文件」 참고)', { title: '⚠ ' + d, variant: 'warning', solid: true, autoHideDelay: 8000 });
        }
      } catch (e) {
        console.error('날짜 일괄 다운로드 실패:', e);
        alert('下载失败(다운로드 실패): ' + e.message);
      } finally {
        this.dl.busy = false;
        this.dl.progress = '';
      }
    },
    async remove(r) {
      if (!confirm((r.palletIds || []).join(', ') + ' — 删除这条记录和上传的文件?(이 기록과 올린 파일을 삭제할까요? 출고리스트에 넣은 BL번호는 그대로 둡니다)')) return;
      this.busyId = r._id;
      try {
        const { data } = await axios.post('/api/coupassInvoiceRecord/delete', { id: r._id });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '删除失败');
        await this.load();
      } catch (e) {
        console.error('箱单发票 기록 삭제 실패:', e);
        alert('删除失败(삭제 실패): ' + e.message);
      } finally {
        this.busyId = '';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.inv-rec {
  &__gap { gap: 8px; }
  // 제목 행 고정(2026-10-07 YZY) — 표 상자 안에서 스크롤. 상자 높이 = 화면에서 상단 네비(73px)·탭 줄을 뺀 만큼이라 상자째 화면에 들어온다
  &__table {
    max-height: calc(100vh - 150px);
    overflow-y: auto;
    font-size: 13px;
    // 전역 .table 의 overflow:hidden 이 sticky 를 막는다(표가 스크롤 상자가 됨) → 여기서만 푼다(InvoiceGenerator·InvoiceLicenseList 와 같은 방식)
    > table { overflow: visible; }
    td { vertical-align: middle; }
    > table > thead th { position: sticky; top: 0; z-index: 3; background: #e9ecef; box-shadow: inset 0 -2px 0 #343a40; }
  }
  // 날짜 묶음 — 같은 日期 행들을 굵은 검은 상자로(2026-10-07 YZY)
  &__day {
    > td:first-child { border-left: 3px solid #000; }
    > td:last-child { border-right: 3px solid #000; }
    &--first > td { border-top: 3px solid #000; }
    &--last > td { border-bottom: 3px solid #000; }
  }
  &__coo + &__coo { border-top: 1px dashed #ddd; margin-top: 2px; padding-top: 2px; }
  &__link { cursor: pointer; color: #007bff; }
  &__cmp { max-height: 55vh; overflow-y: auto; }
  &__pre { white-space: pre-wrap; word-break: break-all; }
  &__diff { font-size: 12px; min-width: 440px; td, th { padding: 2px 4px; } td:first-child, th { white-space: nowrap; } td + td { min-width: 150px; word-break: break-word; } }
  &__plt { white-space: normal; word-break: break-word; }
  &__edit { background: #fffbe0; }
  // 货主 구분 — 综合清关 / 单独清关 / COUPASS单独清关
  &__own { min-width: 150px; max-width: 220px; }
  &__own-line { height: 20px; line-height: 20px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  // 좁은 화면(≤576px): 표 행을 카드처럼 세로로(RecipientAliasPanel·LedgerCostManager 무늬) — 390 에서 칸이 좁아 글자가 한 자씩 꺾였다. 칸 이름은 td data-label
  @media (max-width: 576px) {
    &__cards {
      display: block;

      thead { display: none; }
      tbody { display: block; }
      tbody tr {
        display: block;
        padding: 6px 0;
        border-bottom: 1px solid #e1e6ea;
      }
      tbody td {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px 8px;
        border: 0;
        padding: 3px 4px;
        text-align: left;
        white-space: normal;

        &[data-label]::before {
          content: attr(data-label);
          flex: 0 0 72px;
          font-size: 12px;
          color: #7f8c9a;
        }
        // 칸 안 둘째 줄은 칸 이름 폭만큼 들여 값 줄과 맞춘다
        > div, > small { flex: 1 1 100%; padding-left: 80px; }
      }
      td[colspan] { justify-content: center; }
    }
  }
  &__clr {
    display: inline-block; font-size: 12px; font-weight: 600; padding: 0 6px; border-radius: 3px; line-height: 18px;
    color: #1f3a5f; background: #eef3f8; border: 1px solid #c4d3e3;
    &--separate { color: #6b3d00; background: #fff4e0; border-color: #f0c27a; }
    &--separate_coupass { color: #14532d; background: #e7f5ec; border-color: #9fd3b1; }
  }
}
</style>
